import React, { useEffect, useMemo, useRef } from 'react';

interface HubSpotFormProps {
  formId: string;
  className?: string;
  title?: string;
  subtitle?: string;
}

const HubSpotForm: React.FC<HubSpotFormProps> = ({ 
  formId, 
  className = "",
  title = "Register for this event",
  subtitle = "Secure your spot today"
}) => {
  const formRef = useRef<HTMLDivElement>(null);
  const targetId = useMemo(() => `hs-form-${formId}`.replace(/[^a-zA-Z0-9_-]/g, ''), [formId]);

  useEffect(() => {
    let script: HTMLScriptElement | null = null;

    const createForm = () => {
      if (window.hbspt && formRef.current) {
        try {
          // Clear any existing form content
          if (formRef.current) {
            formRef.current.innerHTML = '';
          }
          
          window.hbspt.forms.create({
            region: 'na1',
            portalId: '19658830',
            formId: formId,
            target: `#${targetId}`,
            onFormReady: () => {
              console.log('HubSpot form ready');
            }
          });
        } catch (error) {
          console.error('Error creating HubSpot form:', error);
          if (formRef.current) {
            formRef.current.innerHTML = '<p class="text-red-500 text-center">Error loading registration form. Please try again.</p>';
          }
        }
      }
    };

    // Load HubSpot script if not already loaded
    if (!window.hbspt) {
      script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/embed/v2.js';
      script.async = true;
      script.onload = () => {
        // Wait a bit for the script to fully initialize
        setTimeout(createForm, 100);
      };
      script.onerror = () => {
        console.error('Failed to load HubSpot script');
        if (formRef.current) {
          formRef.current.innerHTML = '<p class="text-red-500 text-center">Failed to load registration form. Please refresh the page.</p>';
        }
      };
      document.head.appendChild(script);
    } else {
      // Script already loaded, create form immediately
      setTimeout(createForm, 100);
    }

    return () => {
      // Cleanup: remove script if we added it
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [formId]);

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-brand-darkgrey mb-2">
          {title}
        </h3>
        <p className="text-brand-grey text-sm">
          {subtitle}
        </p>
      </div>
      
      {/* HubSpot form will be embedded here */}
      <div id={targetId} ref={formRef} className="min-h-[400px]"></div>
      
      <div className="text-center mt-4">
        <p className="text-xs text-brand-grey">
          By registering, you agree to receive communications from us.
        </p>
      </div>
    </div>
  );
};

// Add HubSpot types to window object
declare global {
  interface Window {
    hbspt: {
      forms: {
        create: (config: any) => void;
      };
    };
  }
}

export default HubSpotForm;

